import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import IconButton from "./IconButton";
import SystemIcon from "./SystemIcon";

let openDialogCount = 0;
let originalBodyOverflow = "";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function Dialog({
  open,
  onClose,
  title,
  eyebrow,
  headerAside,
  size = "md",
  className = "",
  bodyClassName = "",
  footer,
  busy = false,
  dismissible = true,
  initialFocusRef,
  children,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && dismissible && !busyRef.current) {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    if (openDialogCount === 0) originalBodyOverflow = document.body.style.overflow;
    openDialogCount += 1;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    const focusFrame = window.requestAnimationFrame(() => {
      const firstFocusable = dialogRef.current?.querySelector(FOCUSABLE_SELECTOR);
      (initialFocusRef?.current || firstFocusable || dialogRef.current)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      openDialogCount = Math.max(0, openDialogCount - 1);
      if (openDialogCount === 0) document.body.style.overflow = originalBodyOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      const returnTarget = returnFocusRef.current;
      if (returnTarget instanceof Element && returnTarget.isConnected && typeof returnTarget.focus === "function") {
        window.requestAnimationFrame(() => returnTarget.focus());
      }
    };
  }, [dismissible, initialFocusRef, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="ds-dialog-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget && !busy) onClose?.();
      }}
    >
      <section
        ref={dialogRef}
        className={`ds-dialog ds-dialog--${size}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="ds-dialog-header">
          <div className="ds-dialog-heading">
            {eyebrow && <span className="ds-dialog-eyebrow">{eyebrow}</span>}
            <h2 className="ds-dialog-title" id={titleId}>{title}</h2>
          </div>
          <div className="ds-dialog-header-actions">
            {headerAside}
            {dismissible ? (
              <IconButton label="닫기" type="button" onClick={onClose} disabled={busy}>
                <SystemIcon name="close" size="medium" />
              </IconButton>
            ) : null}
          </div>
        </header>
        <div className={`ds-dialog-body${bodyClassName ? ` ${bodyClassName}` : ""}`}>
          {children}
        </div>
        {footer && <footer className="ds-dialog-footer">{footer}</footer>}
      </section>
    </div>,
    document.body
  );
}
